import React, { useState, useEffect, useCallback, useRef } from "react";
import "./FlashcardsGame.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import { publishDemoState } from "../utils/demoMode";

const POINTS_PER_CORRECT = 2;
const ROUND_SECONDS = 60;

const randomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const generateProblem = () => {
  const type = randomInt(0, 2);

  if (type === 0) {
    const a = randomInt(1, 20);
    const b = randomInt(1, 20);
    return { prompt: `${a} + ${b}`, answer: a + b };
  }

  if (type === 1) {
    const a = randomInt(1, 20);
    const b = randomInt(0, a);
    return { prompt: `${a} − ${b}`, answer: a - b };
  }

  const a = randomInt(1, 10);
  const b = randomInt(1, 10);
  return { prompt: `${a} × ${b}`, answer: a * b };
};

const FlashcardsGame = ({ onPointsChange, autoStart = false }) => {
  const { t } = useLanguage();
  const { recordGameEvent } = useFirebase();
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const [problem, setProblem] = useState(null);
  const [answerInput, setAnswerInput] = useState("");
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [message, setMessage] = useState("");
  const [feedback, setFeedback] = useState(""); // success | error | ""
  const [cardKey, setCardKey] = useState(0);
  const inputRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const gameOverRef = useRef(false);
  const didAutoStartRef = useRef(false);

  useEffect(() => {
    gameOverRef.current = gameOver;
  }, [gameOver]);

  useEffect(() => {
    publishDemoState({
      game: "flashcards",
      ready: gameStarted && !gameOver,
      prompt: problem?.prompt || null,
      answer: problem?.answer ?? null,
      score,
      gameOver,
    });
  }, [gameStarted, gameOver, problem, score]);

  const clearAdvanceTimer = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };

  const showNextCard = useCallback(() => {
    if (gameOverRef.current) return;
    clearAdvanceTimer();
    setProblem(generateProblem());
    setAnswerInput("");
    setMessage("");
    setFeedback("");
    setCardKey((prev) => prev + 1);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const startGame = useCallback(() => {
    clearAdvanceTimer();
    gameOverRef.current = false;
    setGameStarted(true);
    setGameOver(false);
    setTimeLeft(ROUND_SECONDS);
    setScore(0);
    setAnswered(0);
    setMessage("");
    setFeedback("");
    setProblem(generateProblem());
    setAnswerInput("");
    setCardKey((prev) => prev + 1);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (!autoStart || didAutoStartRef.current) return;
    didAutoStartRef.current = true;
    startGame();
  }, [autoStart, startGame]);

  useEffect(() => {
    if (!gameStarted || gameOver) return undefined;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          gameOverRef.current = true;
          clearAdvanceTimer();
          setGameOver(true);
          setMessage(t("timesUp"));
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, gameOver, t]);

  useEffect(() => () => clearAdvanceTimer(), []);

  const submitAnswer = useCallback(() => {
    if (!gameStarted || gameOver || !problem || feedback) return;

    const trimmed = answerInput.trim();
    if (trimmed === "") return;

    const guessed = Number(trimmed);
    if (Number.isNaN(guessed)) {
      setMessage(t("flashcardsEnterNumber"));
      return;
    }

    setAnswered((prev) => prev + 1);

    if (guessed === problem.answer) {
      setScore((prev) => prev + POINTS_PER_CORRECT);
      onPointsChange(POINTS_PER_CORRECT);
      recordGameEvent("flashcards_correct");
      setMessage(t("flashcardsCorrect"));
      setFeedback("success");
    } else {
      setMessage(t("flashcardsWrong"));
      setFeedback("error");
    }

    advanceTimerRef.current = setTimeout(() => {
      showNextCard();
    }, 900);
  }, [
    gameStarted,
    gameOver,
    problem,
    feedback,
    answerInput,
    onPointsChange,
    recordGameEvent,
    showNextCard,
    t,
  ]);

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitAnswer();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flashcards-game-container">
      <div className="game-card flashcards-card">
        <h1 className="game-title">{t("flashcardsTitle")}</h1>

        <div className="flashcards-game-info">
          <div className="timer-display">
            <span className="timer-label">{t("time")}</span>
            <span
              className={`timer-value ${timeLeft <= 10 && gameStarted ? "timer-warning" : ""}`}
            >
              {formatTime(gameStarted ? timeLeft : ROUND_SECONDS)}
            </span>
          </div>
          <div className="score-display">
            <span className="score-label">{t("score")}</span>
            <span className="score-value">⭐ {score}</span>
          </div>
        </div>

        {message && (
          <div className={`flashcards-message ${feedback || "info"}`}>
            {message}
          </div>
        )}

        {gameOver ? (
          <div className="flashcards-game-over">
            <h2>{t("gameOverTitle")}</h2>
            <p className="final-score">
              {t("finalScore")} ⭐ {score} {t("points")}
            </p>
            <p className="final-words">
              {t("flashcardsAnswered")} {answered}
            </p>
            <button className="new-game-button" onClick={startGame}>
              🎮 {t("playAgain")}
            </button>
          </div>
        ) : !gameStarted ? (
          <div className="flashcards-start-screen">
            <p className="game-instructions">
              {t("flashcardsInstructions")
                .split("\n")
                .map((line, i, arr) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </React.Fragment>
                ))}
            </p>
            <button className="new-game-button" onClick={startGame}>
              🎮 {t("startGame")}
            </button>
          </div>
        ) : (
          <>
            <div key={cardKey} className="flashcards-flip-card">
              <div className="flashcards-prompt" aria-live="polite">
                {problem?.prompt} = ?
              </div>
            </div>

            <div className="flashcards-answer-row">
              <label className="flashcards-answer-label" htmlFor="flashcards-answer">
                {t("flashcardsYourAnswer")}
              </label>
              <input
                id="flashcards-answer"
                ref={inputRef}
                className="flashcards-answer-input"
                type="text"
                inputMode="numeric"
                pattern="-?[0-9]*"
                autoComplete="off"
                value={answerInput}
                onChange={(e) =>
                  setAnswerInput(e.target.value.replace(/[^\d-]/g, ""))
                }
                onKeyDown={handleInputKeyDown}
                disabled={Boolean(feedback)}
                placeholder="0"
              />
              <button
                type="button"
                className="flashcards-submit"
                onClick={submitAnswer}
                disabled={Boolean(feedback) || answerInput.trim() === ""}
              >
                {t("flashcardsCheck")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlashcardsGame;
